import { useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { LogIn, UserPlus, Mail, KeyRound, Github, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/providers/AuthProvider';
import { cn } from '@/lib/utils';

// Password strength calculation
interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { label: 'At least 8 characters', test: p => p.length >= 8 },
  { label: 'One uppercase letter', test: p => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: p => /[a-z]/.test(p) },
  { label: 'One number', test: p => /\d/.test(p) },
  { label: 'One special character (!@#$%^&*)', test: p => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

type PasswordStrength = 'weak' | 'fair' | 'good' | 'strong';

const getPasswordStrength = (password: string): { strength: PasswordStrength; score: number } => {
  if (!password) return { strength: 'weak', score: 0 };

  const passedRequirements = PASSWORD_REQUIREMENTS.filter(req => req.test(password)).length;
  const score = (passedRequirements / PASSWORD_REQUIREMENTS.length) * 100;

  if (passedRequirements <= 2) return { strength: 'weak', score };
  if (passedRequirements === 3) return { strength: 'fair', score };
  if (passedRequirements === 4) return { strength: 'good', score };
  return { strength: 'strong', score };
};

const strengthConfig: Record<PasswordStrength, { color: string; bgColor: string; label: string }> =
  {
    weak: { color: 'bg-red-500', bgColor: 'bg-red-100', label: 'Weak' },
    fair: { color: 'bg-yellow-500', bgColor: 'bg-yellow-100', label: 'Fair' },
    good: { color: 'bg-green-500', bgColor: 'bg-green-100', label: 'Good' },
    strong: { color: 'bg-green-600', bgColor: 'bg-green-100', label: 'Strong' },
  };

// Password Strength Indicator Component
const PasswordStrengthIndicator = ({ password }: { password: string }) => {
  const { strength, score } = useMemo(() => getPasswordStrength(password), [password]);
  const config = strengthConfig[strength];

  if (!password) return null;

  return (
    <div className="mt-2 space-y-2" role="status" aria-live="polite">
      {/* Strength meter bar */}
      <div className="space-y-1">
        <div className="flex justify-between items-center text-xs">
          <span className="text-muted-foreground">Password strength</span>
          <span
            className={cn('font-medium', {
              'text-red-600': strength === 'weak',
              'text-yellow-600': strength === 'fair',
              'text-green-600': strength === 'good' || strength === 'strong',
            })}
          >
            {config.label}
          </span>
        </div>
        <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden">
          <div
            className={cn('h-full transition-all duration-300 rounded-full', config.color)}
            style={{ width: `${score}%` }}
            role="progressbar"
            aria-valuenow={score}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Password strength: ${config.label}`}
          />
        </div>
      </div>

      {/* Requirements checklist */}
      <div className="grid grid-cols-1 gap-1 text-xs" role="list">
        {PASSWORD_REQUIREMENTS.map((req, index) => {
          const passed = req.test(password);
          return (
            <div
              key={index}
              role="listitem"
              aria-label={`${req.label}: ${passed ? 'met' : 'not met'}`}
              className={cn(
                'flex items-center gap-1.5 transition-colors',
                passed ? 'text-green-600' : 'text-muted-foreground'
              )}
            >
              {passed ? (
                <Check className="h-3 w-3 flex-shrink-0" />
              ) : (
                <X className="h-3 w-3 flex-shrink-0" />
              )}
              <span>{req.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, signIn, signUp, signInWithOAuth, forgotPassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);

  // OAuth loading states
  const [oauthLoading, setOauthLoading] = useState<'google' | 'github' | null>(null);

  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);

  // Computed: any loading state active
  const isAnyLoading = loading || oauthLoading !== null;

  // Redirect if already authenticated
  if (session) {
    navigate('/canvas');
    return null;
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await signIn(email, password);

      if (result.error) {
        throw new Error(result.error);
      }

      toast.success('Welcome back!', {
        description: 'Successfully signed in to Platform Engineering Nexus.',
      });

      // Redirect to canvas (role-based routing can be added later)
      navigate('/canvas');
    } catch (error: any) {
      console.error('Sign in error:', error);
      toast.error('Sign In Failed', {
        description: error.message || 'Invalid credentials. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await signUp(email, password, fullName);

      if (result.error) {
        throw new Error(result.error);
      }

      toast.success('Account Created!', {
        description: 'Your account has been created. You can now sign in.',
      });

      // Auto sign-in after successful sign up
      navigate('/canvas');
    } catch (error: any) {
      console.error('Sign up error:', error);
      toast.error('Sign Up Failed', {
        description: error.message || 'Failed to create account. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await forgotPassword(email);

      if (result.error) {
        throw new Error(result.error);
      }

      toast.success('Reset Link Sent', {
        description: 'Check your email for a password reset link.',
      });
      setResetMode(false);
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast.error('Reset Failed', {
        description: error.message || 'Failed to send reset email. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider: 'google' | 'github') => {
    // Set loading state for this specific provider
    setOauthLoading(provider);

    // Store provider + intended redirect for AuthCallback to use
    localStorage.setItem('pendingOAuthProvider', provider);
    localStorage.setItem(
      'authRedirectTo',
      (location.state as { from?: string })?.from || '/canvas'
    );

    try {
      // Neon Auth OAuth - redirects to OAuth provider
      // Note: This typically redirects, so loading state may persist until redirect
      await signInWithOAuth(provider);
    } catch (error: any) {
      console.error(`${provider} OAuth error:`, error);
      toast.error('OAuth Error', {
        description: `Failed to sign in with ${provider}. Please try again.`,
      });
      // Only clear loading state on error (successful OAuth redirects away)
      setOauthLoading(null);
    }
  };

  if (resetMode) {
    return (
      <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-[var(--foreground)] mb-2">
              Platform Engineering Nexus
            </h1>
            <p className="text-[var(--muted-foreground)]">Reset Your Password</p>
          </div>

          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 p-3 bg-primary/10 rounded-full w-fit">
                <KeyRound className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-xl">Password Reset</CardTitle>
              <p className="text-sm text-muted-foreground">
                Enter your email to receive a reset link
              </p>
            </CardHeader>

            <CardContent>
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email Address</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    disabled={loading}
                    required
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading || !email}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setResetMode(false)}
                    className="text-sm text-primary hover:underline"
                    disabled={loading}
                  >
                    Back to Sign In
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-[var(--foreground)] mb-2">
            Platform Engineering Nexus
          </h1>
          <p className="text-[var(--muted-foreground)]">Make platform complexity visible.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-center text-xl">Welcome</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="signin" className="space-y-4 mt-6">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      disabled={isAnyLoading}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <Input
                      id="signin-password"
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      disabled={isAnyLoading}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isAnyLoading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing In...
                      </>
                    ) : (
                      <>
                        <LogIn className="mr-2 h-4 w-4" />
                        Sign In
                      </>
                    )}
                  </Button>
                </form>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setResetMode(true)}
                    className="text-sm text-primary hover:underline"
                    disabled={isAnyLoading}
                  >
                    Forgot your password?
                  </button>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    onClick={() => handleOAuthSignIn('google')}
                    disabled={isAnyLoading}
                    className="relative"
                  >
                    {oauthLoading === 'google' ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        Google
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleOAuthSignIn('github')}
                    disabled={isAnyLoading}
                    className="relative"
                  >
                    {oauthLoading === 'github' ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Github className="mr-2 h-4 w-4" />
                        GitHub
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="signup" className="space-y-4 mt-6">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Full Name</Label>
                    <Input
                      id="signup-name"
                      value={fullName}
                      onChange={e => setFullName(e.target.value)}
                      placeholder="Enter your full name"
                      disabled={isAnyLoading}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      disabled={isAnyLoading}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Create a password"
                      disabled={isAnyLoading}
                      required
                      minLength={8}
                    />
                    <PasswordStrengthIndicator password={password} />
                  </div>
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="tos-accept"
                      checked={tosAccepted}
                      onCheckedChange={checked => setTosAccepted(checked === true)}
                      disabled={isAnyLoading}
                    />
                    <Label htmlFor="tos-accept" className="text-sm leading-tight">
                      I agree to the{' '}
                      <Link
                        to="/legal/terms-of-service"
                        target="_blank"
                        className="underline text-primary"
                      >
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link
                        to="/legal/privacy-policy"
                        target="_blank"
                        className="underline text-primary"
                      >
                        Privacy Policy
                      </Link>
                    </Label>
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={
                      isAnyLoading ||
                      getPasswordStrength(password).strength === 'weak' ||
                      !tosAccepted
                    }
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      <>
                        <UserPlus className="mr-2 h-4 w-4" />
                        Sign Up
                      </>
                    )}
                  </Button>
                </form>

                {/* OAuth for signup too */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    onClick={() => handleOAuthSignIn('google')}
                    disabled={isAnyLoading}
                    className="relative"
                  >
                    {oauthLoading === 'google' ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        Google
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleOAuthSignIn('github')}
                    disabled={isAnyLoading}
                    className="relative"
                  >
                    {oauthLoading === 'github' ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Github className="mr-2 h-4 w-4" />
                        GitHub
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>

            <div className="text-center mt-6">
              <Link to="/" className="text-sm text-muted-foreground hover:text-primary">
                ← Back to Platform Home
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
